<script setup lang="ts">
import type { MainBannerSlide } from '@/types/main-banner';
import Slide from './banner/Slide.vue';
import 'swiper/css';
import 'swiper/css/pagination';
import type { Swiper as SwiperType } from 'swiper';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Ref, ref } from 'vue';

interface Props {
  slides?: MainBannerSlide[];
}

const props = withDefaults(defineProps<Props>(), {
  slides: () => [],
});

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('ru-RU').format(price) + ' ₽';
};

const swiper = ref<SwiperType | null>(null);

const next = () => swiper.value?.slideNext();
const prev = () => swiper.value?.slidePrev();

const onSwiper = (instance: SwiperType) => {
  swiper.value = instance;
};
</script>

<template>
  <section id="main_banner" class="relative h-screen w-full lg:h-full">
    <Slide v-if="slides.length === 1" :slide="slides[0]" is-main-h1 />

    <Swiper
      v-else
      :modules="[Pagination, EffectFade]"
      :autoplay="{ delay: 5000, disableOnInteraction: false }"
      :fade-effect="{ crossFade: true }"
      :slides-per-view="1"
      :speed="1500"
      :pagination="{
        el: '.swiper-pagination-two',
        clickable: true,
      }"
      :space-between="10"
      class="h-screen items-stretch lg:h-[50vh] lg:min-h-122"
      :loop="true"
      @swiper="onSwiper"
    >
      <SwiperSlide v-for="(slide, index) in slides" :key="`main-banner-slide-${slide.id}`" class="h-full">
        <Slide :slide="slide" :is-main-h1="index === 0" />
      </SwiperSlide>
    </Swiper>

    <div v-if="slides.length > 1" class="mt-4 flex w-full items-center lg:mt-8">
      <div class="flex w-full items-center justify-center px-6 md:px-10">
        <div class="swiper-pagination-two"></div>
      </div>
    </div>
  </section>
</template>

<style scoped>
:deep(.swiper-pagination-two) {
  width: max-content !important;
  display: flex;
  align-items: center;
  gap: 8px;

  .swiper-pagination-bullet {
    width: 50px;
    height: 4px;
    border-radius: 10px;
    margin: 0 !important;
    background-color: var(--color-secondary-foreground);
    opacity: 1;
    position: relative;
    overflow: hidden;
    cursor: pointer;

    &::before {
      position: absolute;
      content: '';
      display: block;
      height: 100%;
      width: 0;
      background: var(--color-primary);
    }

    &.swiper-pagination-bullet-active::before {
      animation-name: countingBar;
      animation-duration: 5s;
      animation-iteration-count: 1;
      animation-timing-function: linear;
      animation-play-state: running;
      animation-fill-mode: both;
    }

    @media (min-width: 768px) {
      width: 70px;
    }
    @media (min-width: 2560px) {
      width: 110px;
    }
    @media (min-width: 3840px) {
      width: 160px;
    }
  }
}

@keyframes countingBar {
  0% {
    width: 0;
  }
  100% {
    width: 100%;
  }
}

:deep(.swiper-slide) {
  transition-property: opacity, transform !important;
}
</style>
